import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import ToolTip from '../ToolTip';
import './DeficiencyIcon.css';

/**
 * DeficiencyIcon Component
 *
 * Displays a clickable link icon on report rows that are linked to a deficiency.
 * Clicking the icon opens a dropdown menu listing the other linked reports
 * (Report / Quote / Work Order) — always excluding the current row's own type —
 * that are completed (have a PDF). Selecting an option opens that report's PDF
 * in a new browser tab.
 *
 * Options can be supplied two ways:
 * - `options`: a resolved array of { key, label, url } (preferred; no fetch).
 * - `getOptions`: an async resolver (deficiency) => Promise<options[]> (fallback).
 * If neither is provided, the icon falls back to a legacy `onClick(deficiency)`.
 */
const DeficiencyIcon = (props) => {
  const {
    deficiency,
    options: providedOptions,
    getOptions,
    onOpenReport,
    onClick,
    className,
    tooltipText,
    intl,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedOptions, setFetchedOptions] = useState(null);
  const containerRef = useRef(null);

  const hasProvidedOptions = Array.isArray(providedOptions);
  // Dropdown mode when options are provided directly or via an async resolver.
  const isDropdownMode = hasProvidedOptions || typeof getOptions === 'function';
  const options = hasProvidedOptions ? providedOptions : fetchedOptions;

  // Close on outside click
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const loadOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      const resolved = await getOptions(deficiency);
      setFetchedOptions(Array.isArray(resolved) ? resolved : []);
    } catch (err) {
      console.error('[DeficiencyIcon] Error loading report options:', err);
      setFetchedOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [getOptions, deficiency]);

  // Don't render if no deficiency data
  if (!deficiency || !deficiency._id) {
    return null;
  }

  const handleToggle = (e) => {
    e.stopPropagation(); // Prevent row click event
    if (!isDropdownMode) {
      // Legacy behavior: delegate the click to the parent handler.
      if (onClick) {
        onClick(deficiency);
      }
      return;
    }
    const next = !isOpen;
    setIsOpen(next);
    // Only fetch when options were not provided directly.
    if (next && !hasProvidedOptions && typeof getOptions === 'function') {
      loadOptions();
    }
  };

  const handleOptionClick = (e, option) => {
    e.stopPropagation();
    setIsOpen(false);
    if (onOpenReport) {
      onOpenReport(option);
    } else if (option && option.url) {
      window.open(option.url, '_blank', 'noopener,noreferrer');
    }
  };

  const tooltip =
    tooltipText ||
    intl.formatMessage({
      id: 'deficiency.icon.tooltip',
      defaultMessage: 'View linked reports',
    });

  const tooltipId = `deficiency-tooltip-${deficiency._id}`;

  const renderMenuContent = () => {
    if (isLoading) {
      return (
        <div className="deficiency-dropdown-item deficiency-dropdown-empty">
          {intl.formatMessage({
            id: 'deficiency.dropdown.loading',
            defaultMessage: 'Loading…',
          })}
        </div>
      );
    }
    if (!options || options.length === 0) {
      return (
        <div className="deficiency-dropdown-item deficiency-dropdown-empty">
          {intl.formatMessage({
            id: 'deficiency.dropdown.empty',
            defaultMessage: 'No linked reports available',
          })}
        </div>
      );
    }
    return options.map((option) => (
      <button
        key={option.key}
        type="button"
        className="deficiency-dropdown-item"
        onClick={(e) => handleOptionClick(e, option)}
      >
        {intl.formatMessage({ id: option.label, defaultMessage: option.label })}
      </button>
    ));
  };

  return (
    <span className="deficiency-dropdown" ref={containerRef}>
      <button
        type="button"
        className={`deficiency-icon no-title ${className || ''}`}
        onClick={handleToggle}
        aria-label={tooltip}
        aria-haspopup={isDropdownMode ? 'menu' : undefined}
        aria-expanded={isDropdownMode ? isOpen : undefined}
        data-for={tooltipId}
        data-tip={tooltip}
      >
        <span className="material-icons">link</span>
      </button>
      <ToolTip id={tooltipId} place="top" />
      {isDropdownMode && isOpen && (
        <div className="deficiency-dropdown-menu" role="menu">
          {renderMenuContent()}
        </div>
      )}
    </span>
  );
};

DeficiencyIcon.propTypes = {
  deficiency: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    status: PropTypes.string,
    description: PropTypes.string,
    reportCategory: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  // Dropdown mode (preferred): resolved array of { key, label, url }
  options: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  // Dropdown mode: async (deficiency) => Promise<Array<{ key, label, url }>>
  getOptions: PropTypes.func,
  // Optional override for opening a report option (dropdown mode)
  onOpenReport: PropTypes.func,
  // Legacy mode: simple click handler (deficiency) => void
  onClick: PropTypes.func,
  className: PropTypes.string,
  tooltipText: PropTypes.string,
  // Injected by react-intl's injectIntl
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
};

DeficiencyIcon.defaultProps = {
  deficiency: null,
  options: null,
  getOptions: null,
  onOpenReport: null,
  onClick: null,
  className: '',
  tooltipText: '',
};

export default injectIntl(DeficiencyIcon);
